import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Loader2, Star, CheckCircle2, Users, Clock, Gift, CreditCard, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { emptyJobFields } from "@/lib/jobFields";
import { JobFields } from "@/components/JobFields";

const STATUS = {
  draft: { label: "Draft — unpaid", color: "#F5A623" },
  pending_review: { label: "In review", color: "#F5A623" },
  published: { label: "Published", color: "#10B981" },
  expired: { label: "Expired", color: "#71717A" },
};

const TABS = [
  { key: "all", label: "All Postings" },
  { key: "draft", label: "Drafts" },
  { key: "published", label: "Published" },
];

export default function AdminJobs() {
  const [tab, setTab] = useState("all");
  const [jobs, setJobs] = useState(null);
  const [packages, setPackages] = useState([]);
  const [payJob, setPayJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [f, setF] = useState({ title: "", description: "", ...emptyJobFields });
  const [pdf, setPdf] = useState(null);

  const load = () => {
    setJobs(null);
    const params = tab === "all" ? {} : { status: tab };
    api.get("/jobs", { params }).then(({ data }) => setJobs(data));
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setPackages(data.job_posting_packages));
    api.get("/organizations").then(({ data }) => setOrgs(data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!orgId) { toast.error("Select the client organization for this posting"); return; }
    setSaving(true);
    try {
      const { data: job } = await api.post("/jobs", { ...f, organization_id: orgId });
      if (pdf) {
        const fd = new FormData();
        fd.append("file", pdf);
        await api.post(`/jobs/${job.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Draft created — publish it free or with a package");
      setOpen(false);
      setF({ title: "", description: "", ...emptyJobFields });
      setPdf(null);
      setOrgId("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const waivePublish = async (id) => {
    try { await api.post(`/jobs/${id}/publish`); toast.success("Published free — candidates notified"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const feature = async (id, current) => {
    try {
      const { data } = await api.post(`/jobs/${id}/feature`);
      toast.success(data.featured ? "Marked as Featured" : "Removed from Featured");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This permanently removes the posting from the board.`)) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Posting deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const payPublish = async (packageId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { kind: "job_posting", package_id: packageId, job_id: payJob.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div>
      <PageHeader overline="Postings, payment & review" title="Jobs">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-job-btn" className="rounded-none bg-primary hover:bg-[#09090B] transition-colors"><Plus className="h-4 w-4 mr-1" /> New posting</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New Job Posting</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Client organization</Label>
                <Select value={orgId} onValueChange={setOrgId}>
                  <SelectTrigger data-testid="admin-job-org" className="bg-[#F4F4F5] border-black/10 rounded-none"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent className="bg-white border-black/10 max-h-64">
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <JobFields values={f} setValues={setF} pdf={pdf} setPdf={setPdf} />
              <DialogFooter><Button type="submit" data-testid="admin-job-save-btn" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create draft"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <div className="px-8 border-b border-black/10 flex gap-6 bg-white sticky top-16 z-10">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`jobtab-${t.key}`} onClick={() => setTab(t.key)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-[#09090B]" : "border-transparent text-muted-foreground hover:text-[#09090B]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-8">
        {!jobs && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {jobs && jobs.length === 0 && <Empty icon={Briefcase} title="Nothing here" hint="New postings start as drafts. Publish & pay, or waive the fee to publish free." />}
        {jobs && jobs.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {jobs.map((j) => {
              const st = STATUS[j.posting_status] || STATUS.draft;
              const isDraft = j.posting_status === "draft" || j.posting_status === "pending_review";
              return (
                <div key={j.id} className="bg-white p-5 flex items-center justify-between" data-testid={`ajob-${j.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{j.title}</p>
                      {j.featured && <Star className="h-3.5 w-3.5 text-primary fill-primary" />}
                      {j.paid && <span className="text-[0.55rem] font-mono text-[#10B981] border border-[#10B981]/30 px-1.5">PAID</span>}
                      {j.comped && <span className="text-[0.55rem] font-mono text-[#F5A623] border border-[#F5A623]/40 px-1.5">COMPED</span>}
                    </div>
                    <p className="overline text-[0.55rem] mt-1">{j.organization_name} · {j.role_category} · <Users className="h-3 w-3 inline" /> {j.interest_count} interested</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: st.color }}><Clock className="h-3.5 w-3.5" /> {st.label}</span>
                    <button onClick={() => feature(j.id)} data-testid={`feature-${j.id}`} className={`p-2 border border-black/10 hover:bg-black/5 transition-colors ${j.featured ? "text-primary" : "text-muted-foreground"}`} title="Toggle featured">
                      <Star className={`h-4 w-4 ${j.featured ? "fill-primary" : ""}`} />
                    </button>
                    <button onClick={() => remove(j.id, j.title)} data-testid={`delete-${j.id}`} className="p-2 border border-black/10 text-muted-foreground hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors" title="Delete posting">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {isDraft && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => waivePublish(j.id)} data-testid={`waive-${j.id}`} className="rounded-none border border-black/10 hover:bg-black/5">
                          <Gift className="h-4 w-4 mr-1" /> Waive & publish
                        </Button>
                        <Button size="sm" onClick={() => setPayJob(j)} data-testid={`pay-${j.id}`} className="rounded-none bg-primary hover:bg-[#09090B]">
                          <CreditCard className="h-4 w-4 mr-1" /> Publish & pay
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!payJob} onOpenChange={(o) => !o && setPayJob(null)}>
        <DialogContent className="bg-white border-black/10 rounded-none">
          <DialogHeader><DialogTitle className="font-heading">Publish & pay</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {packages.map((p) => (
              <button key={p.id} data-testid={`admin-pkg-${p.id}`} disabled={busy} onClick={() => payPublish(p.id)}
                className="border border-black/10 p-5 text-left hover:border-primary hover:bg-[#F4F4F5] transition-colors disabled:opacity-50">
                <div className="flex items-baseline justify-between">
                  <p className="font-heading font-bold text-sm flex items-center gap-1">{p.name.replace(" Posting", "")}{p.id === "featured" && <Star className="h-3 w-3 text-primary fill-primary" />}</p>
                  <p className="font-heading font-black text-xl">${p.price}</p>
                </div>
                <p className="text-[0.6rem] text-muted-foreground mt-1">{p.features[0]}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">Secure Stripe checkout. Goes live instantly after payment. Prefer no charge? Use "Waive & publish".</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
